import { supabase } from './supabase';
import { storagePeriodPrefix } from './storage-path';

/**
 * Uploads an invoice PDF to Supabase Storage and updates the database record.
 *
 * @param orgId - The owning organisation id (used for the storage folder layout)
 * @param invoiceId - The ID of the invoice
 * @param invoiceNumber - The human-readable invoice number
 * @param pdfBase64 - The base64 encoded PDF content
 */
export async function uploadInvoicePDF(
    orgId: string,
    invoiceId: string,
    invoiceNumber: string,
    pdfBase64: string
) {
    try {
        // Shared bucket layout: {org}/{YYYY}/Q{quarter}/{MM}/Invoice_<number>.pdf
        // Stable leaf name + upsert so regenerating an invoice overwrites in place.
        //
        // The org id MUST be the first segment — the invoices-issued RLS policies
        // match `(storage.foldername(name))[1]` against is_org_member(), so this
        // is the tenant boundary, not just a filing convention. The edge-function
        // writer (supabase/functions/_shared/storage-path.ts) builds the same path.
        const fileName = `Invoice_${invoiceNumber || invoiceId}.pdf`;
        const filePath = `${storagePeriodPrefix(orgId)}/${fileName}`;

        // Convert base64 to Blob
        const binaryStr = atob(pdfBase64);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) {
            bytes[i] = binaryStr.charCodeAt(i);
        }

        // 1. Upload to bucket
        const { error: uploadError } = await supabase.storage
            .from('invoices-issued')
            .upload(filePath, bytes, {
                contentType: 'application/pdf',
                upsert: true
            });

        if (uploadError) throw uploadError;

        // 2. Update Invoices Table
        const { error: updateError } = await supabase
            .from('invoices')
            .update({ pdf_storage_path: filePath })
            .eq('id', invoiceId);

        if (updateError) throw updateError;

        console.log(`Stored invoice PDF: ${filePath}`);
        return { success: true, filePath };
    } catch (error) {
        console.error("Failed to store invoice PDF:", error);
        return { success: false, error };
    }
}
